import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import Navbar from "./Navbar";
import NavbarWithPushContent from "./NavbarWithPushContent";
import Footer from "./Footer";
import LoadingSpinner from "../Assets/LoadingSpinner";
import { BlogPost } from "../Types/Types";
import { useTranslation } from "react-i18next";

type ProductCategory = {
  _id: string;
  name: string;
  subcategories: { name: string; products: any[] }[];
  products: any[];
};

interface BlogDetailProps {
  products: ProductCategory[];
  lang: string;
}

const BlogDetail: React.FC<BlogDetailProps> = ({ products, lang }) => {
  const { t } = useTranslation();
  const { id } = useParams<{ id: string }>();
  const [item, setItem] = useState<BlogPost | null>(null);
  const [loading, setLoading] = useState(true);

  const apiUrl = process.env.REACT_APP_API_URL;

  useEffect(() => {
    if (!id) return;
    (async () => {
      setLoading(true);
      try {
        const res = await fetch(`${apiUrl}/api/blog/${id}`);
        if (res.ok) setItem(await res.json());
      } finally {
        setLoading(false);
      }
    })();
  }, [id, apiUrl]);

  return (
    <div className="flex flex-col min-h-screen bg-white font-montserrat">
      <div className="hidden md:block">
        <NavbarWithPushContent products={products} lang={lang} />
      </div>
      <div className="block md:hidden">
        <Navbar products={products} lang={lang} />
      </div>

      <main className="flex-grow px-4 pt-28 md:pt-32 pb-16">
        {loading ? (
          <LoadingSpinner />
        ) : item ? (
          <article className="max-w-3xl mx-auto">
            <Link to="/blog" className="text-brand-500 hover:text-brand-600 font-medium">
              ← {t("blog.header")}
            </Link>
            <h1 className="mt-4 text-3xl sm:text-4xl font-bold text-brand-gray leading-tight">
              {item.title?.[lang]}
            </h1>

            <div className="mt-4 flex items-center gap-3">
              {item.author?.image && (
                <img
                  src={item.author.image}
                  alt={item.author.name}
                  className="h-12 w-12 rounded-full object-cover"
                />
              )}
              <div>
                <p className="text-sm font-semibold text-brand-gray">
                  {t("blog.by")} {item.author?.name}
                </p>
                {item.author?.title?.[lang] && (
                  <p className="text-xs text-gray-500">{item.author.title[lang]}</p>
                )}
              </div>
            </div>

            {item.image && (
              <img
                src={item.image}
                alt={item.title?.[lang]}
                className="mt-6 w-full rounded-2xl shadow-lg object-cover"
              />
            )}
            <div
              className="prose max-w-none mt-8 text-gray-700 ql-editor"
              style={{ padding: 0 }}
              dangerouslySetInnerHTML={{ __html: item.content?.[lang] || "" }}
            />
          </article>
        ) : (
          <p className="text-center text-gray-600 text-lg mt-12">{t("blog.empty")}</p>
        )}
      </main>
      <Footer />
    </div>
  );
};

export default BlogDetail;
